import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrIconComponent } from '../ui/icon/icon.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EditSessionView } from './edit-session';

/**
 * The page header's Save / Cancel / Undo, and the yellow "This edit session"
 * strip listing what the session holds.
 *
 * It belongs to the page header and NOT to either panel, because the table, the
 * map and the Copilot share one session: a Save button inside the table would
 * imply there is a second one somewhere for the map.
 *
 * Standalone, so a consumer imports it directly and `app.module.ts` — which
 * several people edit at once — is not touched to adopt it.
 *
 *     <app-edit-session-bar [session]="session" (saved)="onSaved()">
 *     </app-edit-session-bar>
 */
@Component({
  selector: 'app-edit-session-bar',
  standalone: true,
  imports: [CommonModule, BrIconComponent, MatTooltipModule],
  templateUrl: './edit-session-bar.component.html',
  styleUrls: ['./edit-session-bar.component.css'],
})
export class EditSessionBarComponent {
  /** The page's session, seen through the shared read-and-command surface: the
   *  bar renders labels and origins, which every operation carries, and knows
   *  nothing else about the page's state or operation type. */
  @Input() session: EditSessionView | null = null;

  @Input() saveLabel = 'Save';
  /** Workflow pages can defer Save until the confirmation step. */
  @Input() showSave = true;
  @Input() disabled = false;
  @Output() cancelled = new EventEmitter<void>();

  /** Hide the change list and keep only the controls (a page that draws its own
   *  list, e.g. beside the Copilot transcript). */
  @Input() showChanges = true;

  /** A successful save. The session has already cleared its stack by now. */
  @Output() saved = new EventEmitter<void>();

  /** A failed save. The edits are still in the session; the user can retry. */
  @Output() saveFailed = new EventEmitter<unknown>();

  onSave(): void {
    const session = this.session;
    if (!this.showSave || this.disabled || !session || !session.dirty || session.saving) return;
    session.save().subscribe({
      next: () => this.saved.emit(),
      // Swallowed rather than rethrown: `session.saveError$` already carries it
      // into the banner below, and an unhandled rejection here would be noise on
      // a failure the user is being told about and can retry.
      error: err => this.saveFailed.emit(err),
    });
  }

  onCancel(): void {
    if (this.disabled) return;
    this.session?.cancel();
    this.cancelled.emit();
  }

  onUndo(): void {
    if (!this.disabled) this.session?.undo();
  }

  onUndoAt(index: number): void {
    if (!this.disabled) this.session?.undoAt(index);
  }

  /**
   * The reason a save was refused, in the server's own words where it gave any.
   *
   * `error.error` is read as well as `error.message` because that is the shape
   * our own 400s take - `return BadRequest(new { error = ex.Message })` - and
   * without it the one thing the API took care to say arrived as "Save
   * failed". That was survivable while a page previewed the refusal before
   * anyone pressed Save; on a page that does not, this string is the only
   * explanation there is.
   */
  errorText(err: unknown): string {
    if (!err) return '';
    const anyErr = err as { error?: { message?: string; error?: string }; message?: string };
    return anyErr?.error?.message
      || anyErr?.error?.error
      || anyErr?.message
      || 'Save failed. Your changes are still here.';
  }
}
